export interface DocumentItem {
  id: string;
  type: string;
  title: string;
  number: string;
  projectId: string | null;
  projectName: string | null;
  primaryBinding: string;
  primaryBindingType: "project" | "cta" | "area" | "uc" | "compensation" | "none";
  additionalBindings: string[];
  municipality: string;
  uf: string;
  responsible: string;
  issuingBody: string;
  issueDate: string;
  expiryDate: string | null;
  status: string;
  description: string;
  version: string;
  replacedById: string | null;
  fileType: string;
  hasFile: boolean;
  tags: string[];
  gisMetadata: GisMetadata | null;
  ctaCode: string | null;
  areaId: string | null;
  areaName: string | null;
  inspectionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface GisMetadata {
  srid: string;
  geometry: string;
  featuresCount: number;
  hasValidGeometry: boolean;
  hasMetadata: boolean;
  bbox: string;
}

export interface GisBase {
  id: string;
  layerName: string;
  fileType: string;
  srid: string;
  geometry: string;
  featuresCount: number;
  importDate: string;
  projectId: string;
  projectName: string;
  status: string;
  hasValidGeometry: boolean;
  hasMetadata: boolean;
  documentId: string;
}

export interface Authorization {
  id: string;
  type: string;
  number: string;
  title: string;
  ctaCode: string | null;
  areaId: string | null;
  areaName: string | null;
  issuingBody: string;
  responsibleTech: string;
  issueDate: string;
  expiryDate: string;
  status: string;
  documentId: string;
}

export interface DocumentReport {
  id: string;
  title: string;
  description: string;
  lastGenerated: string | null;
  status: "disponível" | "gerando" | "pendente";
  format: string;
  type: string;
}

export interface DocumentQualityMetric {
  label: string;
  value: number;
  target: number;
  unit: string;
  status: "bom" | "regular" | "ruim";
}

export interface ExpirationItem {
  id: string;
  title: string;
  type: string;
  binding: string;
  expiryDate: string;
  daysRemaining: number;
  priority: "crítico" | "atenção" | "cautela";
  action: string;
  documentId: string;
}

export const documentTypes = [
  "Inventário arbóreo",
  "FCA — Ficha Cadastral Arbórea",
  "Laudo fitossanitário",
  "Parecer técnico",
  "Relatório de vistoria",
  "Relatório fotográfico",
  "Autorização ambiental",
  "Ordem de serviço",
  "Termo de abertura de projeto",
  "Metodologia de inventário",
  "Base cartográfica",
  "Shapefile",
  "GeoJSON",
  "Mapa PDF",
  "Ortofoto",
  "Decreto/lei de criação",
  "Plano de manejo",
  "Memorial descritivo",
  "Compensação ambiental",
  "Relatório de carbono",
  "Evidência fotográfica",
  "Documento externo",
  "Outro",
];

export const documentStatuses = [
  "Rascunho",
  "Em revisão",
  "Validado",
  "Reprovado",
  "Vencido",
  "Substituído",
  "Arquivado",
  "Sem vínculo",
];

export const documentCategories = [
  { id: "projeto", label: "Documentos de Projeto" },
  { id: "arvore", label: "Documentos por Árvore CTA" },
  { id: "ambiental", label: "Fontes Ambientais" },
  { id: "gis", label: "Bases GIS" },
  { id: "autorizacao", label: "Autorizações e Laudos" },
];

export const documents: DocumentItem[] = [
  {
    id: "doc-001",
    type: "Termo de abertura de projeto",
    title: "Termo de Abertura — Inventário Arbóreo Goiânia Norte",
    number: "TAP-2025-001",
    projectId: "prj-001",
    projectName: "Inventário Arbóreo Goiânia Norte",
    primaryBinding: "Inventário Arbóreo Goiânia Norte",
    primaryBindingType: "project",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2025-03-15",
    expiryDate: null,
    status: "Validado",
    description: "Termo de abertura formal do projeto de inventário arbóreo da região norte de Goiânia, definindo escopo, metodologia e equipe técnica.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["projeto", "abertura", "inventário"],
    gisMetadata: null,
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2025-03-15",
    updatedAt: "2026-06-28",
  },
  {
    id: "doc-002",
    type: "Metodologia de inventário",
    title: "Metodologia de Inventário — Protocolo CTA Urbano Goiânia",
    number: "MET-2025-042",
    projectId: "prj-001",
    projectName: "Inventário Arbóreo Goiânia Norte",
    primaryBinding: "Inventário Arbóreo Goiânia Norte",
    primaryBindingType: "project",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2025-03-20",
    expiryDate: null,
    status: "Validado",
    description: "Documento metodológico detalhando protocolos de coleta, critérios de inclusão, parâmetros dendrométricos e equações alométricas utilizadas.",
    version: "2.1",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["metodologia", "protocolo", "CTA"],
    gisMetadata: null,
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2025-03-20",
    updatedAt: "2026-05-10",
  },
  {
    id: "doc-003",
    type: "Inventário arbóreo",
    title: "Inventário Arbóreo — Planilha Consolidada Goiânia Norte",
    number: "INV-2025-089",
    projectId: "prj-001",
    projectName: "Inventário Arbóreo Goiânia Norte",
    primaryBinding: "Inventário Arbóreo Goiânia Norte",
    primaryBindingType: "project",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Téc. Ambiental Ana Souza",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2025-08-10",
    expiryDate: null,
    status: "Validado",
    description: "Planilha consolidada com dados de 3.240 árvores inventariadas na região norte de Goiânia: espécie, DAP, altura, fitossanidade e coordenadas.",
    version: "3.0",
    replacedById: null,
    fileType: "XLSX",
    hasFile: true,
    tags: ["inventário", "dados", "planilha"],
    gisMetadata: null,
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2025-08-10",
    updatedAt: "2026-06-28",
  },
  {
    id: "doc-004",
    type: "Relatório de vistoria",
    title: "Relatório de Vistoria — Setor Jardim Goiás — Junho 2026",
    number: "VIS-2026-112",
    projectId: "prj-001",
    projectName: "Inventário Arbóreo Goiânia Norte",
    primaryBinding: "CTA-GO-001",
    primaryBindingType: "cta",
    additionalBindings: ["CTA-GO-002", "CTA-GO-018"],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-06-20",
    expiryDate: "2026-12-20",
    status: "Validado",
    description: "Vistoria técnica de 32 árvores no Setor Jardim Goiás com avaliação fitossanitária, risco estrutural e recomendações de manejo.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["vistoria", "fitossanidade", "manejo"],
    gisMetadata: null,
    ctaCode: "CTA-GO-001",
    areaId: null,
    areaName: null,
    inspectionId: "insp-012",
    createdAt: "2026-06-20",
    updatedAt: "2026-06-20",
  },
  {
    id: "doc-005",
    type: "Relatório fotográfico",
    title: "Relatório Fotográfico — Parque Municipal Buriti Sereno",
    number: "FOT-2026-056",
    projectId: "prj-006",
    projectName: "Parque Municipal Buriti Sereno",
    primaryBinding: "Parque Municipal Buriti Sereno",
    primaryBindingType: "area",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Biólogo João Rocha",
    issuingBody: "Secretaria de Meio Ambiente",
    issueDate: "2026-05-15",
    expiryDate: null,
    status: "Validado",
    description: "Registro fotográfico do patrimônio arbóreo do parque com 340 fotos georreferenciadas de árvores selecionadas.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["fotográfico", "parque", "registro"],
    gisMetadata: null,
    ctaCode: null,
    areaId: "ga-007",
    areaName: "Parque Municipal Buriti Sereno",
    inspectionId: null,
    createdAt: "2026-05-15",
    updatedAt: "2026-05-15",
  },
  {
    id: "doc-006",
    type: "FCA — Ficha Cadastral Arbórea",
    title: "FCA — Mangifera indica — CTA-GO-001",
    number: "FCA-2025-4501",
    projectId: "prj-001",
    projectName: "Inventário Arbóreo Goiânia Norte",
    primaryBinding: "CTA-GO-001",
    primaryBindingType: "cta",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Téc. Ambiental Ana Souza",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2025-05-10",
    expiryDate: null,
    status: "Validado",
    description: "Ficha cadastral arbórea completa da mangueira (Mangifera indica) localizada na Praça Jardim Goiás, com dados dendrométricos e fotografias.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["FCA", "mangueira", "cadastro"],
    gisMetadata: null,
    ctaCode: "CTA-GO-001",
    areaId: "ga-001",
    areaName: "Praça Jardim Goiás",
    inspectionId: null,
    createdAt: "2025-05-10",
    updatedAt: "2025-05-10",
  },
  {
    id: "doc-007",
    type: "Laudo fitossanitário",
    title: "Laudo Fitossanitário — Jatobá Centenário — CTA-GO-006",
    number: "LAU-2026-033",
    projectId: "prj-003",
    projectName: "Compensação Reserva Ecológica Buritis",
    primaryBinding: "CTA-GO-006",
    primaryBindingType: "cta",
    additionalBindings: ["Reserva Ecológica Buritis"],
    municipality: "Aparecida de Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-04-12",
    expiryDate: "2027-04-12",
    status: "Validado",
    description: "Laudo fitossanitário detalhado do jatobá centenário com análise de integridade estrutural, presença de pragas e avaliação de risco.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["fitossanitário", "jatobá", "risco"],
    gisMetadata: null,
    ctaCode: "CTA-GO-006",
    areaId: "ga-004",
    areaName: "Reserva Ecológica Buritis",
    inspectionId: "insp-008",
    createdAt: "2026-04-12",
    updatedAt: "2026-04-12",
  },
  {
    id: "doc-008",
    type: "Parecer técnico",
    title: "Parecer Técnico — Risco de Queda — CTA-GO-013",
    number: "PAR-2026-018",
    projectId: "prj-006",
    projectName: "Parque Municipal Buriti Sereno",
    primaryBinding: "CTA-GO-013",
    primaryBindingType: "cta",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-06-10",
    expiryDate: null,
    status: "Em revisão",
    description: "Parecer técnico sobre risco de queda da goiabeira com inclinação acentuada próxima à área de playground do parque.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["parecer", "risco", "queda"],
    gisMetadata: null,
    ctaCode: "CTA-GO-013",
    areaId: "ga-007",
    areaName: "Parque Municipal Buriti Sereno",
    inspectionId: "insp-015",
    createdAt: "2026-06-10",
    updatedAt: "2026-06-15",
  },
  {
    id: "doc-009",
    type: "Autorização ambiental",
    title: "Autorização de Poda — CTA-GO-004 — Cedro",
    number: "AUT-2026-072",
    projectId: "prj-002",
    projectName: "Arborização Loteamento Jardim Verde",
    primaryBinding: "CTA-GO-004",
    primaryBindingType: "cta",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "Secretaria Municipal de Meio Ambiente",
    issueDate: "2026-03-01",
    expiryDate: "2026-09-01",
    status: "Validado",
    description: "Autorização ambiental para poda de galhos secos e levantamento de copa do cedro localizado no Jardim América.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["autorização", "poda", "cedro"],
    gisMetadata: null,
    ctaCode: "CTA-GO-004",
    areaId: "ga-003",
    areaName: "Área Verde Jardim América",
    inspectionId: null,
    createdAt: "2026-03-01",
    updatedAt: "2026-03-01",
  },
  {
    id: "doc-010",
    type: "Autorização ambiental",
    title: "Autorização de Supressão — Árvores Exóticas — Loteamento Jardim Verde",
    number: "AUT-2025-198",
    projectId: "prj-002",
    projectName: "Arborização Loteamento Jardim Verde",
    primaryBinding: "Arborização Loteamento Jardim Verde",
    primaryBindingType: "project",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Incorporadora VerdeVida",
    issuingBody: "Secretaria Municipal de Meio Ambiente",
    issueDate: "2025-11-20",
    expiryDate: "2026-11-20",
    status: "Validado",
    description: "Autorização para supressão de 12 árvores exóticas com compensação ambiental de 500 mudas nativas.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["autorização", "supressão", "compensação"],
    gisMetadata: null,
    ctaCode: null,
    areaId: "ga-003",
    areaName: "Área Verde Jardim América",
    inspectionId: null,
    createdAt: "2025-11-20",
    updatedAt: "2025-11-20",
  },
  {
    id: "doc-011",
    type: "Ordem de serviço",
    title: "Ordem de Serviço — Poda Emergencial — Setor Oeste",
    number: "OS-2026-044",
    projectId: "prj-004",
    projectName: "Reflorestamento APP Córrego Matinha",
    primaryBinding: "CTA-GO-008",
    primaryBindingType: "cta",
    additionalBindings: ["CTA-GO-009"],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-06-27",
    expiryDate: "2026-07-27",
    status: "Em revisão",
    description: "Ordem de serviço para poda emergencial de galhos com risco de queda sobre a rede elétrica no Setor Oeste.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["ordem de serviço", "poda", "emergencial"],
    gisMetadata: null,
    ctaCode: "CTA-GO-008",
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2026-06-27",
    updatedAt: "2026-06-27",
  },
  {
    id: "doc-012",
    type: "Base cartográfica",
    title: "Base Cartográfica — Limite Municipal Goiânia — SIRGAS 2000",
    number: "GIS-2025-003",
    projectId: null,
    projectName: null,
    primaryBinding: "Município de Goiânia",
    primaryBindingType: "none",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Geógrafo Marcos Oliveira",
    issuingBody: "IBGE",
    issueDate: "2025-01-15",
    expiryDate: null,
    status: "Validado",
    description: "Base cartográfica do limite municipal de Goiânia em formato shapefile, projeção SIRGAS 2000 UTM 22S.",
    version: "1.0",
    replacedById: null,
    fileType: "SHP",
    hasFile: true,
    tags: ["cartográfica", "limite", "municipal", "GIS"],
    gisMetadata: { srid: "SIRGAS 2000 / UTM 22S (EPSG:31982)", geometry: "Polígono", featuresCount: 1, hasValidGeometry: true, hasMetadata: true, bbox: "-49.35,-16.82,-49.08,-16.55" },
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2025-01-15",
    updatedAt: "2025-01-15",
  },
  {
    id: "doc-013",
    type: "Shapefile",
    title: "Shapefile — Árvores Georreferenciadas — Goiânia Norte",
    number: "GIS-2025-018",
    projectId: "prj-001",
    projectName: "Inventário Arbóreo Goiânia Norte",
    primaryBinding: "Inventário Arbóreo Goiânia Norte",
    primaryBindingType: "project",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Geógrafo Marcos Oliveira",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2025-08-15",
    expiryDate: null,
    status: "Validado",
    description: "Shapefile com 3.240 pontos georreferenciados das árvores inventariadas, com tabela de atributos completa.",
    version: "2.0",
    replacedById: null,
    fileType: "SHP",
    hasFile: true,
    tags: ["shapefile", "árvores", "georreferenciado", "GIS"],
    gisMetadata: { srid: "SIRGAS 2000 / UTM 22S (EPSG:31982)", geometry: "Ponto", featuresCount: 3240, hasValidGeometry: true, hasMetadata: true, bbox: "-49.30,-16.70,-49.25,-16.65" },
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2025-08-15",
    updatedAt: "2026-06-28",
  },
  {
    id: "doc-014",
    type: "GeoJSON",
    title: "GeoJSON — Áreas Verdes Cadastradas — Goiânia",
    number: "GIS-2026-007",
    projectId: "prj-006",
    projectName: "Parque Municipal Buriti Sereno",
    primaryBinding: "Áreas Verdes Goiânia",
    primaryBindingType: "area",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Geógrafo Marcos Oliveira",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-02-10",
    expiryDate: null,
    status: "Validado",
    description: "Arquivo GeoJSON com polígonos de 64 áreas verdes do município de Goiânia, incluindo metadados de área e classificação.",
    version: "1.0",
    replacedById: null,
    fileType: "GEOJSON",
    hasFile: true,
    tags: ["geojson", "áreas verdes", "polígonos", "GIS"],
    gisMetadata: { srid: "WGS 84 (EPSG:4326)", geometry: "Polígono", featuresCount: 64, hasValidGeometry: true, hasMetadata: true, bbox: "-49.35,-16.82,-49.08,-16.55" },
    ctaCode: null,
    areaId: null,
    areaName: "Áreas Verdes Goiânia",
    inspectionId: null,
    createdAt: "2026-02-10",
    updatedAt: "2026-02-10",
  },
  {
    id: "doc-015",
    type: "Ortofoto",
    title: "Ortofoto — Aerolevantamento — Goiânia 2025",
    number: "GIS-2025-025",
    projectId: null,
    projectName: null,
    primaryBinding: "Município de Goiânia",
    primaryBindingType: "none",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Geógrafo Marcos Oliveira",
    issuingBody: "Prefeitura de Goiânia",
    issueDate: "2025-06-20",
    expiryDate: null,
    status: "Validado",
    description: "Ortofoto de alta resolução (10 cm/pixel) do município de Goiânia obtida por aerolevantamento com VANT.",
    version: "1.0",
    replacedById: null,
    fileType: "TIF",
    hasFile: true,
    tags: ["ortofoto", "aerolevantamento", "imagem", "GIS"],
    gisMetadata: { srid: "SIRGAS 2000 / UTM 22S (EPSG:31982)", geometry: "Raster", featuresCount: 1, hasValidGeometry: true, hasMetadata: true, bbox: "-49.38,-16.85,-49.05,-16.52" },
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2025-06-20",
    updatedAt: "2025-06-20",
  },
  {
    id: "doc-016",
    type: "Mapa PDF",
    title: "Mapa de Cobertura Arbórea — Região Central — Goiânia",
    number: "MAP-2026-012",
    projectId: "prj-001",
    projectName: "Inventário Arbóreo Goiânia Norte",
    primaryBinding: "Inventário Arbóreo Goiânia Norte",
    primaryBindingType: "project",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Geógrafo Marcos Oliveira",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-04-05",
    expiryDate: null,
    status: "Validado",
    description: "Mapa temático em PDF da cobertura arbórea da região central com classificação por espécie e estado fitossanitário.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["mapa", "cobertura", "temático"],
    gisMetadata: null,
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2026-04-05",
    updatedAt: "2026-04-05",
  },
  {
    id: "doc-017",
    type: "Decreto/lei de criação",
    title: "Decreto Municipal nº 8.742 — Criação do Parque Buriti Sereno",
    number: "DEC-2018-8742",
    projectId: "prj-006",
    projectName: "Parque Municipal Buriti Sereno",
    primaryBinding: "Parque Municipal Buriti Sereno",
    primaryBindingType: "uc",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Secretaria Municipal de Governo",
    issuingBody: "Prefeitura de Goiânia",
    issueDate: "2018-11-25",
    expiryDate: null,
    status: "Validado",
    description: "Decreto municipal de criação do Parque Municipal Buriti Sereno como Unidade de Conservação de Proteção Integral.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["decreto", "criação", "UC", "parque"],
    gisMetadata: null,
    ctaCode: null,
    areaId: "ga-007",
    areaName: "Parque Municipal Buriti Sereno",
    inspectionId: null,
    createdAt: "2018-11-25",
    updatedAt: "2024-03-10",
  },
  {
    id: "doc-018",
    type: "Plano de manejo",
    title: "Plano de Manejo — Parque Municipal Buriti Sereno — 2024-2029",
    number: "PMN-2024-003",
    projectId: "prj-006",
    projectName: "Parque Municipal Buriti Sereno",
    primaryBinding: "Parque Municipal Buriti Sereno",
    primaryBindingType: "uc",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "Secretaria de Meio Ambiente",
    issueDate: "2024-01-20",
    expiryDate: "2029-01-20",
    status: "Validado",
    description: "Plano de manejo quinquenal do parque com zoneamento, programas de conservação, uso público e monitoramento ambiental.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["plano de manejo", "UC", "zoneamento"],
    gisMetadata: null,
    ctaCode: null,
    areaId: "ga-007",
    areaName: "Parque Municipal Buriti Sereno",
    inspectionId: null,
    createdAt: "2024-01-20",
    updatedAt: "2024-01-20",
  },
  {
    id: "doc-019",
    type: "Memorial descritivo",
    title: "Memorial Descritivo — Área de Compensação Reserva Buritis",
    number: "MEM-2025-026",
    projectId: "prj-003",
    projectName: "Compensação Reserva Ecológica Buritis",
    primaryBinding: "Reserva Ecológica Buritis",
    primaryBindingType: "area",
    additionalBindings: [],
    municipality: "Aparecida de Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "Construtora Horizonte",
    issueDate: "2025-09-10",
    expiryDate: null,
    status: "Validado",
    description: "Memorial descritivo da área de compensação ambiental com caracterização da vegetação, hidrografia e delimitação georreferenciada.",
    version: "2.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["memorial", "compensação", "caracterização"],
    gisMetadata: null,
    ctaCode: null,
    areaId: "ga-004",
    areaName: "Reserva Ecológica Buritis",
    inspectionId: null,
    createdAt: "2025-09-10",
    updatedAt: "2026-01-15",
  },
  {
    id: "doc-020",
    type: "Compensação ambiental",
    title: "Termo de Compensação Ambiental — Loteamento Jardim Verde",
    number: "TCA-2025-033",
    projectId: "prj-002",
    projectName: "Arborização Loteamento Jardim Verde",
    primaryBinding: "Arborização Loteamento Jardim Verde",
    primaryBindingType: "compensation",
    additionalBindings: ["Área Verde Jardim América"],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Incorporadora VerdeVida",
    issuingBody: "Secretaria Municipal de Meio Ambiente",
    issueDate: "2025-11-25",
    expiryDate: "2028-11-25",
    status: "Validado",
    description: "Termo de compromisso de compensação ambiental com plantio de 500 mudas nativas e monitoramento por 36 meses.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["compensação", "termo", "mudas"],
    gisMetadata: null,
    ctaCode: null,
    areaId: "ga-003",
    areaName: "Área Verde Jardim América",
    inspectionId: null,
    createdAt: "2025-11-25",
    updatedAt: "2025-11-25",
  },
  {
    id: "doc-021",
    type: "Relatório de carbono",
    title: "Relatório de Carbono — Avaliação Preliminar — Goiânia Norte",
    number: "CAR-2026-008",
    projectId: "prj-001",
    projectName: "Inventário Arbóreo Goiânia Norte",
    primaryBinding: "Inventário Arbóreo Goiânia Norte",
    primaryBindingType: "project",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-06-15",
    expiryDate: null,
    status: "Validado",
    description: "Relatório preliminar de carbono com estimativas de CO2e, biomassa arbórea e potencial de sequestro de carbono do projeto.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["carbono", "CO2e", "preliminar"],
    gisMetadata: null,
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2026-06-15",
    updatedAt: "2026-06-15",
  },
  {
    id: "doc-022",
    type: "Evidência fotográfica",
    title: "Evidência Fotográfica — Poda CTA-GO-004 — Antes e Depois",
    number: "EVI-2026-091",
    projectId: "prj-002",
    projectName: "Arborização Loteamento Jardim Verde",
    primaryBinding: "CTA-GO-004",
    primaryBindingType: "cta",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Téc. Ambiental Ana Souza",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-03-10",
    expiryDate: null,
    status: "Validado",
    description: "Registro fotográfico da poda do cedro com 24 fotos mostrando antes, durante e depois da intervenção.",
    version: "1.0",
    replacedById: null,
    fileType: "ZIP",
    hasFile: true,
    tags: ["evidência", "fotográfica", "poda"],
    gisMetadata: null,
    ctaCode: "CTA-GO-004",
    areaId: null,
    areaName: null,
    inspectionId: "insp-010",
    createdAt: "2026-03-10",
    updatedAt: "2026-03-10",
  },
  {
    id: "doc-023",
    type: "Shapefile",
    title: "Shapefile — Unidades de Conservação — Estado de Goiás",
    number: "GIS-2024-031",
    projectId: null,
    projectName: null,
    primaryBinding: "Estado de Goiás",
    primaryBindingType: "none",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Geógrafo Marcos Oliveira",
    issuingBody: "SEMAD-GO",
    issueDate: "2024-08-10",
    expiryDate: null,
    status: "Validado",
    description: "Shapefile com polígonos das Unidades de Conservação estaduais e municipais do Estado de Goiás, incluindo RPPNs.",
    version: "1.0",
    replacedById: null,
    fileType: "SHP",
    hasFile: true,
    tags: ["shapefile", "UC", "estadual", "GIS"],
    gisMetadata: { srid: "SIRGAS 2000 / UTM 22S (EPSG:31982)", geometry: "Polígono", featuresCount: 87, hasValidGeometry: true, hasMetadata: true, bbox: "-53.25,-19.50,-45.90,-12.40" },
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2024-08-10",
    updatedAt: "2024-08-10",
  },
  {
    id: "doc-024",
    type: "GeoJSON",
    title: "GeoJSON — Pontos CTA — Projeto Parque Buriti Sereno",
    number: "GIS-2026-019",
    projectId: "prj-006",
    projectName: "Parque Municipal Buriti Sereno",
    primaryBinding: "Parque Municipal Buriti Sereno",
    primaryBindingType: "area",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Geógrafo Marcos Oliveira",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-03-22",
    expiryDate: null,
    status: "Em revisão",
    description: "Arquivo GeoJSON com 2.100 pontos do CTA do parque, incluindo atributos de espécie, DAP e fitossanidade.",
    version: "1.0",
    replacedById: null,
    fileType: "GEOJSON",
    hasFile: true,
    tags: ["geojson", "CTA", "pontos", "GIS"],
    gisMetadata: { srid: "WGS 84 (EPSG:4326)", geometry: "Ponto", featuresCount: 2100, hasValidGeometry: true, hasMetadata: false, bbox: "-49.28,-16.72,-49.26,-16.70" },
    ctaCode: null,
    areaId: "ga-007",
    areaName: "Parque Municipal Buriti Sereno",
    inspectionId: null,
    createdAt: "2026-03-22",
    updatedAt: "2026-03-22",
  },
  {
    id: "doc-025",
    type: "Mapa PDF",
    title: "Mapa de Risco Arbóreo — Área Industrial Anápolis",
    number: "MAP-2026-018",
    projectId: "prj-005",
    projectName: "Recuperação Área Degradada Setor Industrial",
    primaryBinding: "Área Recuperada Industrial",
    primaryBindingType: "area",
    additionalBindings: [],
    municipality: "Anápolis",
    uf: "GO",
    responsible: "Geógrafo Marcos Oliveira",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-05-10",
    expiryDate: null,
    status: "Rascunho",
    description: "Mapa temático de classificação de risco arbóreo da área industrial com destaque para árvores críticas.",
    version: "0.9",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["mapa", "risco", "industrial"],
    gisMetadata: null,
    ctaCode: null,
    areaId: "ga-006",
    areaName: "Área Recuperada Industrial",
    inspectionId: null,
    createdAt: "2026-05-10",
    updatedAt: "2026-05-12",
  },
  {
    id: "doc-026",
    type: "Documento externo",
    title: "Norma ABNT NBR 16246 — Inventário Arbóreo Urbano",
    number: "EXT-2024-001",
    projectId: null,
    projectName: null,
    primaryBinding: "Referência normativa",
    primaryBindingType: "none",
    additionalBindings: [],
    municipality: "",
    uf: "",
    responsible: "ABNT",
    issuingBody: "ABNT — Associação Brasileira de Normas Técnicas",
    issueDate: "2024-05-15",
    expiryDate: null,
    status: "Validado",
    description: "Norma técnica brasileira para inventário arbóreo urbano — referência para todos os projetos FloraIQ.",
    version: "2.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["norma", "ABNT", "referência"],
    gisMetadata: null,
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2024-05-15",
    updatedAt: "2024-05-15",
  },
  {
    id: "doc-027",
    type: "Relatório de vistoria",
    title: "Relatório de Vistoria — Compensação Reserva Buritis — Maio 2026",
    number: "VIS-2026-098",
    projectId: "prj-003",
    projectName: "Compensação Reserva Ecológica Buritis",
    primaryBinding: "Reserva Ecológica Buritis",
    primaryBindingType: "compensation",
    additionalBindings: [],
    municipality: "Aparecida de Goiânia",
    uf: "GO",
    responsible: "Biólogo João Rocha",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-05-20",
    expiryDate: "2026-11-20",
    status: "Validado",
    description: "Relatório de vistoria de monitoramento da compensação ambiental com avaliação de sobrevivência de mudas e regeneração natural.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["vistoria", "compensação", "monitoramento"],
    gisMetadata: null,
    ctaCode: null,
    areaId: "ga-004",
    areaName: "Reserva Ecológica Buritis",
    inspectionId: "insp-018",
    createdAt: "2026-05-20",
    updatedAt: "2026-05-20",
  },
  {
    id: "doc-028",
    type: "Laudo fitossanitário",
    title: "Laudo Fitossanitário — Sibipiruna — CTA-GO-002 — Presença de Cupins",
    number: "LAU-2026-041",
    projectId: "prj-001",
    projectName: "Inventário Arbóreo Goiânia Norte",
    primaryBinding: "CTA-GO-002",
    primaryBindingType: "cta",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-05-08",
    expiryDate: "2026-05-08",
    status: "Vencido",
    description: "Laudo fitossanitário com diagnóstico de infestação por cupins na sibipiruna, recomendando tratamento fitossanitário urgente.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["fitossanitário", "cupins", "urgente"],
    gisMetadata: null,
    ctaCode: "CTA-GO-002",
    areaId: "ga-001",
    areaName: "Praça Jardim Goiás",
    inspectionId: "insp-005",
    createdAt: "2026-05-08",
    updatedAt: "2026-05-08",
  },
  {
    id: "doc-029",
    type: "FCA — Ficha Cadastral Arbórea",
    title: "FCA — Hymenaea courbaril — CTA-GO-006",
    number: "FCA-2025-4522",
    projectId: "prj-003",
    projectName: "Compensação Reserva Ecológica Buritis",
    primaryBinding: "CTA-GO-006",
    primaryBindingType: "cta",
    additionalBindings: [],
    municipality: "Aparecida de Goiânia",
    uf: "GO",
    responsible: "Téc. Ambiental Ana Souza",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2025-10-15",
    expiryDate: null,
    status: "Validado",
    description: "Ficha cadastral do jatobá centenário com dados dendrométricos completos e registro fotográfico histórico.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["FCA", "jatobá", "centenário"],
    gisMetadata: null,
    ctaCode: "CTA-GO-006",
    areaId: "ga-004",
    areaName: "Reserva Ecológica Buritis",
    inspectionId: null,
    createdAt: "2025-10-15",
    updatedAt: "2025-10-15",
  },
  {
    id: "doc-030",
    type: "Parecer técnico",
    title: "Parecer Jurídico-Ambiental — Compensação APP Córrego Matinha",
    number: "PAR-2026-024",
    projectId: "prj-004",
    projectName: "Reflorestamento APP Córrego Matinha",
    primaryBinding: "APP Córrego Matinha",
    primaryBindingType: "area",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Dra. Mariana Castro — OAB/GO 45.678",
    issuingBody: "Castro Advocacia Ambiental",
    issueDate: "2026-04-28",
    expiryDate: null,
    status: "Validado",
    description: "Parecer jurídico-ambiental sobre a compensação da APP do Córrego Matinha, analisando conformidade com legislação federal e municipal.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["jurídico", "APP", "conformidade"],
    gisMetadata: null,
    ctaCode: null,
    areaId: "ga-005",
    areaName: "APP Córrego Matinha",
    inspectionId: null,
    createdAt: "2026-04-28",
    updatedAt: "2026-04-28",
  },
  {
    id: "doc-031",
    type: "Ordem de serviço",
    title: "Ordem de Serviço — Supressão Árvores Exóticas — Loteamento Jardim Verde",
    number: "OS-2026-035",
    projectId: "prj-002",
    projectName: "Arborização Loteamento Jardim Verde",
    primaryBinding: "Arborização Loteamento Jardim Verde",
    primaryBindingType: "project",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Incorporadora VerdeVida",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-02-10",
    expiryDate: "2026-03-10",
    status: "Substituído",
    description: "Ordem de serviço original para supressão — substituída pela OS-2026-038 com ajustes no escopo.",
    version: "1.0",
    replacedById: "doc-032",
    fileType: "PDF",
    hasFile: true,
    tags: ["ordem de serviço", "supressão", "substituída"],
    gisMetadata: null,
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2026-02-10",
    updatedAt: "2026-02-28",
  },
  {
    id: "doc-032",
    type: "Ordem de serviço",
    title: "Ordem de Serviço — Supressão Árvores Exóticas — Revisada",
    number: "OS-2026-038",
    projectId: "prj-002",
    projectName: "Arborização Loteamento Jardim Verde",
    primaryBinding: "Arborização Loteamento Jardim Verde",
    primaryBindingType: "project",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Incorporadora VerdeVida",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-02-28",
    expiryDate: "2026-03-28",
    status: "Validado",
    description: "Ordem de serviço revisada com ajuste de escopo — redução de 15 para 12 árvores exóticas a serem suprimidas.",
    version: "2.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["ordem de serviço", "supressão", "revisada"],
    gisMetadata: null,
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: null,
    createdAt: "2026-02-28",
    updatedAt: "2026-02-28",
  },
  {
    id: "doc-033",
    type: "Relatório de carbono",
    title: "Relatório de Carbono — Avaliação Preliminar — Parque Buriti Sereno",
    number: "CAR-2026-012",
    projectId: "prj-006",
    projectName: "Parque Municipal Buriti Sereno",
    primaryBinding: "Parque Municipal Buriti Sereno",
    primaryBindingType: "uc",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-06-25",
    expiryDate: null,
    status: "Em revisão",
    description: "Relatório preliminar de carbono do parque municipal com estimativas baseadas em 2.100 árvores inventariadas.",
    version: "0.5",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["carbono", "CO2e", "parque"],
    gisMetadata: null,
    ctaCode: null,
    areaId: "ga-007",
    areaName: "Parque Municipal Buriti Sereno",
    inspectionId: null,
    createdAt: "2026-06-25",
    updatedAt: "2026-06-28",
  },
  {
    id: "doc-034",
    type: "Evidência fotográfica",
    title: "Evidência Fotográfica — Vistoria Compensação — Buritis Maio 2026",
    number: "EVI-2026-105",
    projectId: "prj-003",
    projectName: "Compensação Reserva Ecológica Buritis",
    primaryBinding: "Reserva Ecológica Buritis",
    primaryBindingType: "compensation",
    additionalBindings: [],
    municipality: "Aparecida de Goiânia",
    uf: "GO",
    responsible: "Biólogo João Rocha",
    issuingBody: "FloraIQ Engenharia Ambiental",
    issueDate: "2026-05-22",
    expiryDate: null,
    status: "Sem vínculo",
    description: "Evidências fotográficas da vistoria de monitoramento com 86 fotos georreferenciadas das mudas plantadas.",
    version: "1.0",
    replacedById: null,
    fileType: "ZIP",
    hasFile: false,
    tags: ["evidência", "fotográfica", "vistoria"],
    gisMetadata: null,
    ctaCode: null,
    areaId: null,
    areaName: null,
    inspectionId: "insp-018",
    createdAt: "2026-05-22",
    updatedAt: "2026-05-22",
  },
  {
    id: "doc-035",
    type: "Autorização ambiental",
    title: "Autorização de Transplante — CTA-GO-012 — Cupiúba",
    number: "AUT-2026-081",
    projectId: "prj-006",
    projectName: "Parque Municipal Buriti Sereno",
    primaryBinding: "CTA-GO-012",
    primaryBindingType: "cta",
    additionalBindings: [],
    municipality: "Goiânia",
    uf: "GO",
    responsible: "Eng. Florestal Carlos Mendes",
    issuingBody: "Secretaria Municipal de Meio Ambiente",
    issueDate: "2026-04-15",
    expiryDate: "2026-07-15",
    status: "Validado",
    description: "Autorização para transplante da cupiúba localizada em área de futura intervenção no parque, com plano de translocação.",
    version: "1.0",
    replacedById: null,
    fileType: "PDF",
    hasFile: true,
    tags: ["autorização", "transplante", "cupiúba"],
    gisMetadata: null,
    ctaCode: "CTA-GO-012",
    areaId: "ga-007",
    areaName: "Parque Municipal Buriti Sereno",
    inspectionId: null,
    createdAt: "2026-04-15",
    updatedAt: "2026-04-15",
  },
];

export const gisBases: GisBase[] = [
  { id: "gis-001", layerName: "Limite Municipal Goiânia", fileType: "SHP", srid: "SIRGAS 2000 / UTM 22S (EPSG:31982)", geometry: "Polígono", featuresCount: 1, importDate: "2025-01-15", projectId: "", projectName: "Base institucional", status: "Validado", hasValidGeometry: true, hasMetadata: true, documentId: "doc-012" },
  { id: "gis-002", layerName: "Árvores CTA Goiânia Norte", fileType: "SHP", srid: "SIRGAS 2000 / UTM 22S (EPSG:31982)", geometry: "Ponto", featuresCount: 3240, importDate: "2025-08-15", projectId: "prj-001", projectName: "Inventário Arbóreo Goiânia Norte", status: "Validado", hasValidGeometry: true, hasMetadata: true, documentId: "doc-013" },
  { id: "gis-003", layerName: "Áreas Verdes Goiânia", fileType: "GeoJSON", srid: "WGS 84 (EPSG:4326)", geometry: "Polígono", featuresCount: 64, importDate: "2026-02-10", projectId: "prj-006", projectName: "Parque Municipal Buriti Sereno", status: "Validado", hasValidGeometry: true, hasMetadata: true, documentId: "doc-014" },
  { id: "gis-004", layerName: "Ortofoto Goiânia 2025", fileType: "TIF", srid: "SIRGAS 2000 / UTM 22S (EPSG:31982)", geometry: "Raster", featuresCount: 1, importDate: "2025-06-20", projectId: "", projectName: "Base institucional", status: "Validado", hasValidGeometry: true, hasMetadata: true, documentId: "doc-015" },
  { id: "gis-005", layerName: "Unidades de Conservação GO", fileType: "SHP", srid: "SIRGAS 2000 / UTM 22S (EPSG:31982)", geometry: "Polígono", featuresCount: 87, importDate: "2024-08-10", projectId: "", projectName: "Base institucional", status: "Validado", hasValidGeometry: true, hasMetadata: true, documentId: "doc-023" },
  { id: "gis-006", layerName: "Pontos CTA Parque Buriti Sereno", fileType: "GeoJSON", srid: "WGS 84 (EPSG:4326)", geometry: "Ponto", featuresCount: 2100, importDate: "2026-03-22", projectId: "prj-006", projectName: "Parque Municipal Buriti Sereno", status: "Em revisão", hasValidGeometry: true, hasMetadata: false, documentId: "doc-024" },
  { id: "gis-007", layerName: "Bairros Goiânia", fileType: "SHP", srid: "SIRGAS 2000 / UTM 22S (EPSG:31982)", geometry: "Polígono", featuresCount: 698, importDate: "2024-11-05", projectId: "", projectName: "Base institucional", status: "Validado", hasValidGeometry: true, hasMetadata: true, documentId: "" },
  { id: "gis-008", layerName: "APP Urbana Córrego Matinha", fileType: "KML", srid: "WGS 84 (EPSG:4326)", geometry: "Polígono", featuresCount: 1, importDate: "2025-12-10", projectId: "prj-004", projectName: "Reflorestamento APP Córrego Matinha", status: "Pendente validação", hasValidGeometry: false, hasMetadata: false, documentId: "" },
];

export const authorizations: Authorization[] = [
  { id: "auth-001", type: "Autorização de poda", number: "AUT-2026-072", title: "Autorização de Poda — CTA-GO-004 — Cedro", ctaCode: "CTA-GO-004", areaId: "ga-003", areaName: "Área Verde Jardim América", issuingBody: "Secretaria Municipal de Meio Ambiente", responsibleTech: "Eng. Florestal Carlos Mendes", issueDate: "2026-03-01", expiryDate: "2026-09-01", status: "Válida", documentId: "doc-009" },
  { id: "auth-002", type: "Autorização de supressão", number: "AUT-2025-198", title: "Autorização de Supressão — Árvores Exóticas", ctaCode: null, areaId: "ga-003", areaName: "Área Verde Jardim América", issuingBody: "Secretaria Municipal de Meio Ambiente", responsibleTech: "Eng. Florestal Carlos Mendes", issueDate: "2025-11-20", expiryDate: "2026-11-20", status: "Válida", documentId: "doc-010" },
  { id: "auth-003", type: "Autorização de transplante", number: "AUT-2026-081", title: "Autorização de Transplante — CTA-GO-012 — Cupiúba", ctaCode: "CTA-GO-012", areaId: "ga-007", areaName: "Parque Municipal Buriti Sereno", issuingBody: "Secretaria Municipal de Meio Ambiente", responsibleTech: "Eng. Florestal Carlos Mendes", issueDate: "2026-04-15", expiryDate: "2026-07-15", status: "Válida", documentId: "doc-035" },
  { id: "auth-004", type: "Laudo fitossanitário", number: "LAU-2026-033", title: "Laudo Fitossanitário — Jatobá Centenário — CTA-GO-006", ctaCode: "CTA-GO-006", areaId: "ga-004", areaName: "Reserva Ecológica Buritis", issuingBody: "FloraIQ Engenharia Ambiental", responsibleTech: "Eng. Florestal Carlos Mendes", issueDate: "2026-04-12", expiryDate: "2027-04-12", status: "Válida", documentId: "doc-007" },
  { id: "auth-005", type: "Laudo fitossanitário", number: "LAU-2026-041", title: "Laudo Fitossanitário — Sibipiruna — CTA-GO-002", ctaCode: "CTA-GO-002", areaId: "ga-001", areaName: "Praça Jardim Goiás", issuingBody: "FloraIQ Engenharia Ambiental", responsibleTech: "Eng. Florestal Carlos Mendes", issueDate: "2026-05-08", expiryDate: "2026-05-08", status: "Vencida", documentId: "doc-028" },
  { id: "auth-006", type: "Parecer de risco", number: "PAR-2026-018", title: "Parecer Técnico — Risco de Queda — CTA-GO-013", ctaCode: "CTA-GO-013", areaId: "ga-007", areaName: "Parque Municipal Buriti Sereno", issuingBody: "FloraIQ Engenharia Ambiental", responsibleTech: "Eng. Florestal Carlos Mendes", issueDate: "2026-06-10", expiryDate: "", status: "Em revisão", documentId: "doc-008" },
  { id: "auth-007", type: "Parecer jurídico/ambiental", number: "PAR-2026-024", title: "Parecer Jurídico-Ambiental — Compensação APP Córrego Matinha", ctaCode: null, areaId: "ga-005", areaName: "APP Córrego Matinha", issuingBody: "Castro Advocacia Ambiental", responsibleTech: "Dra. Mariana Castro — OAB/GO 45.678", issueDate: "2026-04-28", expiryDate: "", status: "Válida", documentId: "doc-030" },
  { id: "auth-008", type: "Termo de compensação", number: "TCA-2025-033", title: "Termo de Compensação Ambiental — Loteamento Jardim Verde", ctaCode: null, areaId: "ga-003", areaName: "Área Verde Jardim América", issuingBody: "Secretaria Municipal de Meio Ambiente", responsibleTech: "Incorporadora VerdeVida", issueDate: "2025-11-25", expiryDate: "2028-11-25", status: "Válida", documentId: "doc-020" },
];

export const documentReports: DocumentReport[] = [
  { id: "dr-001", title: "Índice Documental do Projeto", description: "Listagem completa de todos os documentos vinculados a um projeto com metadados, status e validade.", lastGenerated: "2026-06-20", status: "disponível", format: "PDF / Excel", type: "índice" },
  { id: "dr-002", title: "Relatório de Fontes por Árvore CTA", description: "Relação de documentos vinculados a cada indivíduo arbóreo do CTA com histórico de vistorias e laudos.", lastGenerated: "2026-06-15", status: "disponível", format: "PDF / Excel", type: "arvore" },
  { id: "dr-003", title: "Relatório de Autorizações Ambientais", description: "Consolidado de autorizações de poda, supressão e transplante com status de validade e execução.", lastGenerated: "2026-06-10", status: "disponível", format: "PDF", type: "autorizacao" },
  { id: "dr-004", title: "Relatório de Vencimentos", description: "Painel de controle com documentos vencidos e a vencer nos próximos 30, 60 e 90 dias.", lastGenerated: "2026-06-28", status: "disponível", format: "PDF / Excel", type: "vencimento" },
  { id: "dr-005", title: "Relatório de Bases GIS", description: "Inventário das bases cartográficas e geoespaciais com indicadores de qualidade e validação geométrica.", lastGenerated: "2026-06-05", status: "disponível", format: "PDF / Excel", type: "gis" },
  { id: "dr-006", title: "Relatório Documental de UC", description: "Documentos vinculados a Unidades de Conservação: planos de manejo, decretos, autorizações e relatórios.", lastGenerated: null, status: "pendente", format: "PDF", type: "uc" },
  { id: "dr-007", title: "Relatório de Evidências Técnicas", description: "Compilação de evidências fotográficas e laudos técnicos para auditoria e conformidade.", lastGenerated: "2026-05-20", status: "disponível", format: "PDF", type: "evidencia" },
  { id: "dr-008", title: "Relatório de Conformidade Documental", description: "Análise da qualidade documental com indicadores de completude, vinculação e conformidade normativa.", lastGenerated: null, status: "pendente", format: "PDF", type: "conformidade" },
];

export const documentQualityMetrics: DocumentQualityMetric[] = [
  { label: "Documentos com vínculo definido", value: 91, target: 95, unit: "%", status: "regular" },
  { label: "Documentos validados", value: 78, target: 90, unit: "%", status: "regular" },
  { label: "Documentos com responsável técnico", value: 85, target: 90, unit: "%", status: "bom" },
  { label: "Documentos com validade definida", value: 62, target: 80, unit: "%", status: "ruim" },
  { label: "Documentos com arquivo anexado", value: 94, target: 95, unit: "%", status: "bom" },
  { label: "Bases GIS com geometria válida", value: 87, target: 95, unit: "%", status: "regular" },
];

export const expirationItems: ExpirationItem[] = [
  { id: "exp-001", title: "Laudo Fitossanitário — Sibipiruna CTA-GO-002", type: "Laudo fitossanitário", binding: "CTA-GO-002", expiryDate: "2026-05-08", daysRemaining: -54, priority: "crítico", action: "Emitir novo laudo fitossanitário com urgência", documentId: "doc-028" },
  { id: "exp-002", title: "Autorização de Transplante — CTA-GO-012 — Cupiúba", type: "Autorização de transplante", binding: "CTA-GO-012", expiryDate: "2026-07-15", daysRemaining: 14, priority: "atenção", action: "Executar transplante ou solicitar renovação", documentId: "doc-035" },
  { id: "exp-003", title: "Autorização de Poda — CTA-GO-004 — Cedro", type: "Autorização de poda", binding: "CTA-GO-004", expiryDate: "2026-09-01", daysRemaining: 62, priority: "cautela", action: "Agendar execução da poda autorizada", documentId: "doc-009" },
  { id: "exp-004", title: "Relatório de Vistoria — Setor Jardim Goiás", type: "Relatório de vistoria", binding: "Jardim Goiás", expiryDate: "2026-12-20", daysRemaining: 172, priority: "cautela", action: "Programar próxima vistoria antes do vencimento", documentId: "doc-004" },
  { id: "exp-005", title: "Relatório de Vistoria — Compensação Reserva Buritis", type: "Relatório de vistoria", binding: "Reserva Ecológica Buritis", expiryDate: "2026-11-20", daysRemaining: 142, priority: "cautela", action: "Agendar vistoria de monitoramento", documentId: "doc-027" },
  { id: "exp-006", title: "Plano de Manejo — Parque Buriti Sereno", type: "Plano de manejo", binding: "Parque Municipal Buriti Sereno", expiryDate: "2029-01-20", daysRemaining: 934, priority: "cautela", action: "Planejar revisão quinquenal", documentId: "doc-018" },
  { id: "exp-007", title: "Autorização de Supressão — Árvores Exóticas", type: "Autorização de supressão", binding: "Loteamento Jardim Verde", expiryDate: "2026-11-20", daysRemaining: 142, priority: "cautela", action: "Concluir supressão e apresentar relatório", documentId: "doc-010" },
  { id: "exp-008", title: "Termo de Compensação Ambiental — Jardim Verde", type: "Termo de compensação", binding: "Loteamento Jardim Verde", expiryDate: "2028-11-25", daysRemaining: 877, priority: "cautela", action: "Continuar monitoramento de sobrevivência", documentId: "doc-020" },
];